import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  createWithdrawal,
  deleteAutoWithdrawSchedule,
  fetchAutoWithdrawSchedule,
  fetchMe,
  fetchMySipayWallet,
  fetchMyWithdrawals,
  upsertAutoWithdrawSchedule,
} from "../Api";
import ExpandableEventCard from "../components/ExpandableEventCard";
import MonthDayPicker from "../components/MonthDayPicker";
import ScheduleTimePicker from "../components/ScheduleTimePicker";

function fmtMoney(v) {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}

function trDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("tr-TR");
  } catch {
    return String(iso);
  }
}

function normalizeIbanInput(v) {
  return String(v || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 26);
}

function normalizeNationalIdInput(v) {
  return String(v || "").replace(/\D/g, "").slice(0, 11);
}

function isFailStatus(status) {
  const s = String(status || "").toLowerCase();
  return s === "payout_failed" || s === "deduct_without_payout" || s === "refunded" || s === "failed";
}

function isPendingStatus(status) {
  const s = String(status || "").toLowerCase();
  return s === "payout_pending" || s === "payout_sending" || s === "awaiting_payout" || s === "processing";
}

const DRIVER_COMPANY_BALANCE_MSG = "Başarısız, yarım saat sonra tekrar deneyiniz.";
const MIN_WITHDRAW_TL = 150;
const MIN_WITHDRAW_MSG = "Çekilebilir bakiye 150 TL'nin altındaysa para çekilemez.";
const MANUAL_WITHDRAW_BANNER =
  "ÖNEMLİ DUYURU\nYandex kaynaklı teknik arıza nedeniyle hakedişleriniz doğru görüntülenememektedir. Bu nedenle para çekme işlemleri geçici olarak durdurulmuştur.\n\nMevcut bakiyeleriniz ve yeni yolculuk hakedişleriniz korunmaktadır. Yandex kaynaklı sorun giderildiğinde tüm hakedişlerinizi görüntüleyebilir ve bakiyenizi çekebilirsiniz.";

function istanbulDateKey(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-CA", { timeZone: "Europe/Istanbul" });
}

function manualWithdrawRulesActive(_now = new Date()) {
  // Geçici günde-bir / otomatik-talimat kısıtı kapatıldı.
  return false;
}

function hasManualWithdrawalToday(items, now = new Date()) {
  const today = istanbulDateKey(now);
  return (items || []).some((row) => {
    if (String(row?.source || "") !== "manual") return false;
    return istanbulDateKey(row?.created_at) === today;
  });
}

function isCompanyBalanceFail(textOrRow) {
  if (textOrRow && typeof textOrRow === "object") {
    if (String(textOrRow.sipay_provider_code || "") === "02") return true;
    return isCompanyBalanceFail(
      String(
        textOrRow.sipay_error ||
          textOrRow.sipay_provider_message ||
          textOrRow.yandex_error ||
          textOrRow.error ||
          ""
      )
    );
  }
  const e = String(textOrRow || "").toLowerCase();
  if (!e) return false;
  if (e.includes("çekilebilir") || e.includes("cekilebilir") || e.includes("yetersiz bakiye (min")) {
    return false;
  }
  return (
    e.includes("faturamatik bakiyesi yetersiz") ||
    e.includes("bakiye yetersiz") ||
    e.includes("yarım saat sonra tekrar") ||
    e.includes("yarim saat sonra tekrar")
  );
}

function looksMaskedPersonName(name) {
  const s = String(name || "").trim();
  if (!s || !s.includes("*")) return false;
  const parts = s.split(/\s+/).filter(Boolean);
  return parts.length > 0 && parts.every((p) => p.includes("*"));
}

function firstUnmaskedHolder(...cands) {
  for (const c of cands) {
    const s = String(c || "").trim();
    if (s && !looksMaskedPersonName(s)) return s;
  }
  return "";
}

function failReasonText(row) {
  if (!isFailStatus(row?.status)) return "";
  if (isCompanyBalanceFail(row)) return DRIVER_COMPANY_BALANCE_MSG;
  if (String(row?.status || "").toLowerCase() === "deduct_without_payout") {
    return String(row?.yandex_refund_error || "Düşüm yapıldı, gönderim yapılmadı.").trim();
  }
  const raw = String(row?.sipay_error || row?.sipay_provider_message || row?.yandex_error || "").trim();
  if (isCompanyBalanceFail(raw)) return DRIVER_COMPANY_BALANCE_MSG;
  return raw;
}

function statusLabel(status, row) {
  const s = String(status || "").toLowerCase();
  const reason = failReasonText({ ...(row || {}), status: s });
  const isWallet = String(row?.payout_method || "").toLowerCase() === "wallet";
  const dest = isWallet ? "Cuzdan" : "IBAN";
  if (s === "paid") return "Odendi";
  if (s === "payout_pending") return `${dest} gonderildi · Banka onayi bekleniyor`;
  if (s === "payout_sending") return `${dest} gonderimi yapiliyor`;
  if (s === "payout_failed") {
    return reason ? `${dest} basarisiz: ${reason}` : `Yandex dustu · ${dest} gonderimi basarisiz`;
  }
  if (s === "deduct_without_payout") {
    return reason || "Dusum yapildi, gonderim yapilmadi";
  }
  if (s === "failed") {
    return reason || "Yandex dusumu basarisiz · gonderim yapilmadi";
  }
  if (s === "refunded") {
    return reason
      ? `${dest} basarisiz · Iade edildi: ${reason}`
      : `${dest} basarisiz · Yandex bakiyeye iade edildi`;
  }
  if (s === "awaiting_payout") return `Bakiye dusuldu · ${dest} gonderimi bekleniyor`;
  if (s === "approved") return "Onaylandi";
  if (s === "rejected") return "Reddedildi";
  if (s === "processing") return "Yandex bakiyeden dusuluyor";
  return "Beklemede";
}

function isIsyerimIbanRow(r) {
  if (String(r?.payout_method || "").toLowerCase() === "wallet") return false;
  const provider = String(r?.payout_provider || "").toLowerCase();
  const ext = String(r?.sipay_ext_transaction_id || "");
  return provider === "isyerimpos" || ext.startsWith("IYB-");
}

function isVakifbankIbanRow(r) {
  if (String(r?.payout_method || "").toLowerCase() === "wallet") return false;
  const provider = String(r?.payout_provider || "").toLowerCase();
  const ext = String(r?.sipay_ext_transaction_id || "");
  return provider === "vakifbank" || ext.startsWith("vbt_");
}

function trackingBits(r) {
  if (isVakifbankIbanRow(r)) {
    const sorgu = String(r?.sipay_request_withdraw_id || "").trim();
    const islem = String(r?.sipay_provider_transaction_id || "").trim();
    const bits = [];
    if (sorgu) bits.push({ label: "VakıfBank SorguNo", value: sorgu });
    if (islem) bits.push({ label: "VakıfBank İşlemNo", value: islem });
    return bits;
  }
  if (isIsyerimIbanRow(r)) {
    const ref = String(r?.sipay_provider_transaction_id || "").trim();
    const odeme = String(r?.sipay_request_withdraw_id || "").trim();
    if (ref) return [{ label: "İşyerimPOS ref", value: ref }];
    if (odeme) return [{ label: "İşyerimPOS ödeme no", value: odeme }];
    return [];
  }
  const ext = String(r?.sipay_ext_transaction_id || "").trim();
  return ext ? [{ label: "Sipay ID", value: ext }] : [];
}

function payoutStepLabel(r) {
  const s = String(r?.status || "").toLowerCase();
  const isWallet = String(r?.payout_method || "").toLowerCase() === "wallet";
  const dest = isWallet ? "cuzdan" : "IBAN";
  const iy = isIsyerimIbanRow(r);
  const vb = isVakifbankIbanRow(r);
  if (s === "processing") return "1/2 Yandex dusumu";
  if (s === "payout_sending") {
    if (vb) return "2/2 IBAN gonderimi (VakıfBank)";
    return iy ? "2/2 IBAN gonderimi (İşyerimPOS)" : `2/2 Sipay ${dest} gonderimi`;
  }
  if (s === "payout_pending") {
    if (vb) return "2/2 VakıfBank kabul etti · banka bekleniyor";
    return "2/2 Sipay kabul etti · banka bekleniyor";
  }
  if (s === "payout_failed") {
    if (vb) return "2/2 VakıfBank IBAN gonderimi basarisiz";
    return iy ? "2/2 IBAN gonderimi basarisiz" : "2/2 Sipay basarisiz";
  }
  if (s === "deduct_without_payout") {
    return "2/2 Gonderim basarisiz · Yandex iade edilemedi";
  }
  if (s === "failed") {
    return "1/2 Yandex dusumu basarisiz";
  }
  if (s === "refunded") {
    if (vb) return "VakıfBank IBAN basarisiz · Yandex iade edildi";
    return iy
      ? "IBAN gonderimi basarisiz · Yandex iade edildi"
      : "Sipay basarisiz · Yandex iade edildi";
  }
  if (s === "paid") return "Tamamlandi";
  if (s === "awaiting_payout") {
    if (vb) return "1/2 Yandex OK · VakıfBank bekleniyor";
    return iy ? "1/2 Yandex OK · IBAN bekleniyor" : "1/2 Yandex OK · Sipay bekleniyor";
  }
  return statusLabel(s, r);
}

function PayoutMethodSwitch({ value, onChange, disabled, ibanLocked }) {
  return (
    <div className="payout-method-switch" role="group" aria-label="Odeme yontemi">
      <button
        type="button"
        className={`payout-method-btn${value === "iban" ? " is-active" : ""}${
          ibanLocked ? " is-maintenance" : ""
        }`}
        disabled={disabled || ibanLocked}
        onClick={() => {
          if (!ibanLocked) onChange("iban");
        }}
      >
        IBAN
        {ibanLocked ? <span className="payout-method-btn-badge">Bakımda</span> : null}
      </button>
      <button
        type="button"
        className={`payout-method-btn${value === "wallet" ? " is-active" : ""}`}
        disabled={disabled}
        onClick={() => onChange("wallet")}
      >
        Sipay Cuzdan
      </button>
    </div>
  );
}

const WEEKDAYS = [
  { value: 0, label: "Pazartesi" },
  { value: 1, label: "Sali" },
  { value: 2, label: "Carsamba" },
  { value: 3, label: "Persembe" },
  { value: 4, label: "Cuma" },
  { value: 5, label: "Cumartesi" },
  { value: 6, label: "Pazar" },
];

function formatHm(h, m) {
  return `${String(h).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
}

function scheduleSummary(s) {
  if (!s || !s.enabled) return "Kapali";
  const t1h = s.morning_hour ?? s.hour ?? 10;
  const t1m = s.morning_minute ?? s.minute ?? 0;
  const t2h = s.evening_hour ?? 20;
  const t2m = s.evening_minute ?? 0;
  const second = s.second_slot_enabled === true;
  const times = second
    ? `${formatHm(t1h, t1m)} ve ${formatHm(t2h, t2m)}`
    : formatHm(t1h, t1m);
  const balanceNote = s.withdraw_full_balance !== false ? " · tum bakiye" : "";
  if (s.frequency === "daily") return `Her gun ${times}${balanceNote}`;
  if (s.frequency === "every_2_days") return `2 günde bir ${times}${balanceNote}`;
  if (s.frequency === "every_3_days") return `3 günde bir ${times}${balanceNote}`;
  if (s.frequency === "weekly") {
    const d = WEEKDAYS.find((x) => x.value === Number(s.weekday))?.label || "-";
    return `Her ${d} ${times}${balanceNote}`;
  }
  if (s.frequency === "monthly") {
    return `Her ayin ${s.day_of_month}. gunu ${times}${balanceNote}`;
  }
  return "-";
}

export default function WithdrawalPage() {
  const FIXED_FEE_IBAN = 6;
  const FIXED_FEE_WALLET = 2;
  const [busy, setBusy] = useState(false);
  const [ibanErr, setIbanErr] = useState("");
  const [ibanOk, setIbanOk] = useState("");
  const [walletErr, setWalletErr] = useState("");
  const [walletOk, setWalletOk] = useState("");
  const [rows, setRows] = useState([]);
  const [withdrawable, setWithdrawable] = useState(0);
  const [payoutsLocked, setPayoutsLocked] = useState(false);
  const [ibanLocked, setIbanLocked] = useState(false);
  const [autoLocked, setAutoLocked] = useState(false);
  const [ibanNationalIdRequired, setIbanNationalIdRequired] = useState(false);

  const [iban, setIban] = useState("");
  const [holder, setHolder] = useState(""); // IBAN manuel — bos baslar
  const [walletHolder, setWalletHolder] = useState(""); // Sipay cuzdan — profil/Sipay'den dolar
  const [nationalId, setNationalId] = useState("");
  const [ibanAccepted, setIbanAccepted] = useState(false);
  const [walletAccepted, setWalletAccepted] = useState(false);
  const [walletNumber, setWalletNumber] = useState("");
  const [walletMasked, setWalletMasked] = useState("");
  const [walletBusy, setWalletBusy] = useState(false);
  const pollTimerRef = useRef(null);

  const [autoBusy, setAutoBusy] = useState(false);
  const [autoErr, setAutoErr] = useState("");
  const [autoOk, setAutoOk] = useState("");
  const [autoFrequency, setAutoFrequency] = useState("daily");
  const [autoTime1Hour, setAutoTime1Hour] = useState(10);
  const [autoTime1Minute, setAutoTime1Minute] = useState(0);
  const [autoTime2Hour, setAutoTime2Hour] = useState(20);
  const [autoTime2Minute, setAutoTime2Minute] = useState(0);
  const [autoSecondSlotEnabled, setAutoSecondSlotEnabled] = useState(false);
  const [autoWeekday, setAutoWeekday] = useState(0);
  const [autoDayOfMonth, setAutoDayOfMonth] = useState(1);
  const [autoIban, setAutoIban] = useState("");
  const [autoHolder, setAutoHolder] = useState("");
  const [autoNationalId, setAutoNationalId] = useState("");
  const [autoAccepted, setAutoAccepted] = useState(false);
  const [autoPayoutMethod, setAutoPayoutMethod] = useState("wallet");
  const [autoWalletNumber, setAutoWalletNumber] = useState("");
  const [autoWalletMasked, setAutoWalletMasked] = useState("");
  const [autoWalletBusy, setAutoWalletBusy] = useState(false);
  const [autoWalletErr, setAutoWalletErr] = useState("");
  const [savedSchedule, setSavedSchedule] = useState(null);

  function stopStatusPoll() {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }

  function profileHolderName(meProfile) {
    const p = meProfile || (() => {
      try {
        return JSON.parse(localStorage.getItem("profile") || "{}");
      } catch {
        return {};
      }
    })();
    return [p?.first_name, p?.last_name]
      .map((x) => String(x || "").trim())
      .filter(Boolean)
      .join(" ")
      .trim();
  }

  function resolveWalletHolderName(data, meProfile) {
    const fromSipay = firstUnmaskedHolder(
      data?.account_holder,
      [data?.first_name, data?.last_name]
        .map((x) => String(x || "").trim())
        .filter(Boolean)
        .join(" ")
        .trim()
    );
    return fromSipay || profileHolderName(meProfile) || "";
  }

  function ibanFormStorageKey() {
    try {
      const p = JSON.parse(localStorage.getItem("profile") || "{}");
      const phone = String(p.phone || p.normalized_phone || "").replace(/\D/g, "");
      return phone ? `sitaxi_iban_form_${phone}` : "sitaxi_iban_form";
    } catch {
      return "sitaxi_iban_form";
    }
  }

  function loadSavedIbanForm() {
    try {
      const raw = JSON.parse(localStorage.getItem(ibanFormStorageKey()) || "null");
      if (!raw || typeof raw !== "object") return null;
      return {
        iban: normalizeIbanInput(raw.iban),
        holder: String(raw.holder || "").trim(),
        nationalId: normalizeNationalIdInput(raw.nationalId),
      };
    } catch {
      return null;
    }
  }

  function saveIbanForm(nextIban, nextHolder, nextNationalId) {
    const ibanVal = normalizeIbanInput(nextIban);
    const holderVal = String(nextHolder || "").trim();
    const tcVal = normalizeNationalIdInput(nextNationalId);
    if (!ibanVal && !holderVal && !tcVal) return;
    try {
      localStorage.setItem(
        ibanFormStorageKey(),
        JSON.stringify({ iban: ibanVal, holder: holderVal, nationalId: tcVal, savedAt: Date.now() })
      );
    } catch {
      /* ignore quota */
    }
  }

  function lastIbanWithdrawal(items) {
    const rows = Array.isArray(items) ? items : [];
    return (
      rows.find(
        (r) =>
          String(r?.payout_method || "iban").toLowerCase() !== "wallet" &&
          /^\s*TR/i.test(String(r?.iban || ""))
      ) || null
    );
  }

  function applyPayoutPrefill(prefill, { fillManual = true, fillAuto = true, items = [], meProfile = null } = {}) {
    const p = prefill || {};
    const saved = loadSavedIbanForm() || {};
    const last = lastIbanWithdrawal(items) || {};
    const profileName = profileHolderName(meProfile);
    const nextIban = normalizeIbanInput(last.iban || p.iban || saved.iban || "");
    const nextHolder = firstUnmaskedHolder(
      last.account_holder,
      p.account_holder,
      saved.holder,
      profileName
    );
    const nextTc = normalizeNationalIdInput(
      last.national_id || p.national_id || saved.nationalId || ""
    );
    if (fillManual) {
      if (nextIban) setIban(nextIban);
      if (nextHolder) setHolder(nextHolder);
      if (nextTc) setNationalId(nextTc);
      const wh = nextHolder || profileName;
      if (wh) setWalletHolder(wh);
    }
    if (fillAuto) {
      if (nextHolder) setAutoHolder(nextHolder);
      else if (profileName) setAutoHolder(profileName);
      if (nextIban) setAutoIban(nextIban);
      if (nextTc) setAutoNationalId(nextTc);
    }
  }

  async function reloadAll() {
    const token = localStorage.getItem("token") || "";
    const me = await fetchMe(token);
    if (me?.profile) {
      try {
        localStorage.setItem("profile", JSON.stringify(me.profile));
      } catch {
        /* ignore */
      }
    }
    const w = Number(
      me?.profile?.withdrawable_balance ??
        me?.profile?.balance_withdrawable ??
        0
    );
    setWithdrawable(Number.isFinite(w) ? w : 0);
    if (typeof me?.payouts_locked === "boolean") setPayoutsLocked(me.payouts_locked);
    if (typeof me?.iban_payouts_locked === "boolean") setIbanLocked(me.iban_payouts_locked);
    if (typeof me?.auto_withdraw_locked === "boolean") setAutoLocked(me.auto_withdraw_locked);
    if (typeof me?.iban_national_id_required === "boolean") {
      setIbanNationalIdRequired(me.iban_national_id_required);
    }
    const data = await fetchMyWithdrawals();
    const items = Array.isArray(data?.items) ? data.items : [];
    setRows(items);
    return { items, me };
  }

  async function reloadSchedule() {
    const data = await fetchAutoWithdrawSchedule();
    const s = data?.schedule || null;
    setSavedSchedule(s);
    if (s) {
      setAutoFrequency(s.frequency || "daily");
      setAutoTime1Hour(Number(s.morning_hour ?? s.hour ?? 10));
      setAutoTime1Minute(Number(s.morning_minute ?? s.minute ?? 0));
      setAutoTime2Hour(Number(s.evening_hour ?? 20));
      setAutoTime2Minute(Number(s.evening_minute ?? 0));
      setAutoSecondSlotEnabled(!!s.second_slot_enabled);
      setAutoWeekday(Number(s.weekday ?? 0));
      setAutoDayOfMonth(Number(s.day_of_month ?? 1));
      setAutoPayoutMethod(s.payout_method === "wallet" ? "wallet" : "iban");
      if (s.iban) setAutoIban(s.iban);
      const realHolder = firstUnmaskedHolder(s.account_holder, profileHolderName());
      if (realHolder) setAutoHolder(realHolder);
      if (s.national_id) setAutoNationalId(normalizeNationalIdInput(s.national_id));
      if (s.wallet_number) setAutoWalletNumber(s.wallet_number);
      if (s.wallet_number_masked) setAutoWalletMasked(s.wallet_number_masked);
    }
    return s;
  }

  async function loadSipayWallet(target = "manual", meProfile) {
    const setBusyFn = target === "auto" ? setAutoWalletBusy : setWalletBusy;
    const setErrFn = target === "auto" ? setAutoWalletErr : setWalletErr;
    setBusyFn(true);
    setErrFn("");
    try {
      const data = await fetchMySipayWallet();
      const wn = String(data?.wallet_number || "");
      const wm = String(data?.wallet_number_masked || wn);
      const wh = resolveWalletHolderName(data, meProfile);
      if (target === "auto") {
        setAutoWalletNumber(wn);
        setAutoWalletMasked(wm);
        if (wh) setAutoHolder(wh);
      } else {
        setWalletNumber(wn);
        setWalletMasked(wm);
        if (wh) setWalletHolder(wh);
      }
      return data;
    } catch (e2) {
      setErrFn(e2?.message || "Sipay cuzdan bulunamadi.");
      if (target === "auto") {
        setAutoWalletNumber("");
        setAutoWalletMasked("");
      } else {
        setWalletNumber("");
        setWalletMasked("");
      }
      // Sipay ad donmese bile profil adini goster
      const fallback = profileHolderName(meProfile);
      if (fallback) {
        if (target === "auto") setAutoHolder(fallback);
        else setWalletHolder(fallback);
      }
      return null;
    } finally {
      setBusyFn(false);
    }
  }

  async function onAutoPayoutMethodChange(next) {
    if (next === "iban" && ibanLocked) return;
    setAutoPayoutMethod(next);
    setAutoErr("");
    setAutoWalletErr("");
    if (next === "wallet") {
      await loadSipayWallet("auto");
    }
  }

  function startStatusPoll(watchId, payoutMethod = "iban") {
    stopStatusPoll();
    let ticks = 0;
    const isWallet = payoutMethod === "wallet";
    pollTimerRef.current = setInterval(async () => {
      ticks += 1;
      try {
        const all = await reloadAll();
        const items = all?.items || [];
        const watched = watchId ? items.find((x) => x.id === watchId) : null;
        const st = String(watched?.status || "").toLowerCase();
        if (watched && !isPendingStatus(st)) {
          if (st === "paid") {
            if (isWallet) {
              setWalletErr("");
              setWalletOk("Başarılı");
            } else {
              setIbanErr("");
              setIbanOk("Başarılı");
            }
          } else if (st === "refunded" || st === "payout_failed" || st === "deduct_without_payout" || st === "failed") {
            const reason = failReasonText(watched) || watched?.sipay_error || watched?.sipay_provider_message || watched?.yandex_error || "";
            if (isWallet) {
              setWalletOk("");
              setWalletErr(reason || (st === "failed" ? "Yandex düşümü başarısız. Gönderim yapılmadı." : "Cüzdan gönderimi başarısız."));
            } else {
              setIbanOk("");
              setIbanErr(reason || (st === "failed" ? "Yandex düşümü başarısız. Gönderim yapılmadı." : "IBAN gönderimi başarısız."));
            }
          }
          stopStatusPoll();
          return;
        }
        if (!items.some((x) => isPendingStatus(x.status))) {
          stopStatusPoll();
        }
      } catch {
        // ignore
      }
      if (ticks >= 40) stopStatusPoll();
    }, 3000);
  }

  useEffect(() => {
    Promise.all([reloadAll(), reloadSchedule()])
      .then(async ([all, schedule]) => {
        const items = all?.items || [];
        const prefill = all?.me?.payout_prefill || {};
        const meProfile = all?.me?.profile || null;
        applyPayoutPrefill(prefill, { fillManual: true, fillAuto: !schedule, items, meProfile });
        const profileName = profileHolderName(meProfile);
        if (profileName) {
          setHolder((cur) => firstUnmaskedHolder(cur, profileName) || cur);
          setAutoHolder((cur) => firstUnmaskedHolder(cur, profileName) || cur);
          setWalletHolder((cur) => firstUnmaskedHolder(cur, profileName) || cur);
        }
        await loadSipayWallet("manual", meProfile);
        if (schedule?.payout_method === "wallet") {
          await loadSipayWallet("auto", meProfile);
        }
        // Otomatik formda kayıtlı talimat yoksa aynı ön doldurma
        if (items.some((x) => isPendingStatus(x.status))) startStatusPoll(null);
      })
      .catch(() => {});
    return () => stopStatusPoll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const amountNum = Number.isFinite(withdrawable) ? withdrawable : 0;
  const belowMinWithdraw = amountNum < MIN_WITHDRAW_TL;
  const feeIban = amountNum > FIXED_FEE_IBAN ? FIXED_FEE_IBAN : 0;
  const feeWallet = amountNum > FIXED_FEE_WALLET ? FIXED_FEE_WALLET : 0;
  const netIban = Math.max(0, amountNum - feeIban);
  const netWallet = Math.max(0, amountNum - feeWallet);
  const nationalOk = nationalId.length === 10 || nationalId.length === 11;
  const walletReady = !!walletNumber;
  const hasActiveSchedule = !!(savedSchedule && savedSchedule.enabled);
  const hasAutoInstruction = !!(hasActiveSchedule && !savedSchedule?.driver_disabled);
  const manualRulesOn = manualWithdrawRulesActive();
  const manualUsedToday = hasManualWithdrawalToday(rows);
  const manualBlocked = manualRulesOn && (hasAutoInstruction || manualUsedToday);
  const manualLockLabel = hasAutoInstruction ? "Otomatik talimat" : "Bugün kullanıldı";
  const canSubmitIban =
    !manualBlocked &&
    ibanAccepted &&
    !belowMinWithdraw &&
    amountNum > FIXED_FEE_IBAN &&
    !!(iban && holder && (!ibanNationalIdRequired || nationalOk));
  const canSubmitWallet =
    !manualBlocked &&
    walletAccepted &&
    !belowMinWithdraw &&
    amountNum > FIXED_FEE_WALLET &&
    walletReady;

  const autoNationalOk = autoNationalId.length === 10 || autoNationalId.length === 11;
  const autoWalletOk = !!autoWalletNumber;
  const autoTimesOk =
    !autoSecondSlotEnabled ||
    autoTime1Hour !== autoTime2Hour ||
    autoTime1Minute !== autoTime2Minute;
  const canSubmitAuto =
    autoAccepted &&
    autoTimesOk &&
    (autoPayoutMethod === "wallet"
      ? autoWalletOk
      : !!(autoIban && autoHolder && (!ibanNationalIdRequired || autoNationalOk)));
  const isAdminMandate = !!(
    hasActiveSchedule &&
    savedSchedule?.source === "admin_mandate" &&
    !savedSchedule?.driver_disabled
  );

  async function submitWithdrawal(payoutMethod, e) {
    e.preventDefault();
    if (busy) return;
    if (payoutMethod === "iban" && ibanLocked) {
      setIbanErr("IBAN para çekimi bakımda.");
      return;
    }
    if (manualBlocked) {
      const setBlockedErr = payoutMethod === "wallet" ? setWalletErr : setIbanErr;
      setBlockedErr(
        hasAutoInstruction
          ? "Otomatik talimatı olan kişiler manuel çekim yapamayacaktır."
          : "Manuel para çekimi 13.10.2026 gününe kadar günde bir kere yapılabilir."
      );
      return;
    }
    const isWallet = payoutMethod === "wallet";
    const setErrFn = isWallet ? setWalletErr : setIbanErr;
    const setOkFn = isWallet ? setWalletOk : setIbanOk;
    setErrFn("");
    setOkFn("");
    try {
      setBusy(true);
      const payload = isWallet
        ? {
            payout_method: "wallet",
            account_holder: walletHolder || profileHolderName() || "Sipay Cuzdan",
            amount: amountNum,
          }
        : {
            payout_method: "iban",
            iban,
            account_holder: holder,
            ...(ibanNationalIdRequired ? { national_id: nationalId } : {}),
            amount: amountNum,
          };
      const res = await createWithdrawal(payload);
      const item = res?.item || {};
      const st = String(item.status || "").toLowerCase();
      const apiErr = String(
        item.sipay_error ||
          item.sipay_provider_message ||
          item.yandex_error ||
          item.yandex_refund_error ||
          failReasonText(item) ||
          res?.error ||
          ""
      ).trim();
      if (st === "paid") {
        setOkFn("Başarılı");
      } else if (st === "payout_failed" || st === "refunded" || st === "failed" || st === "deduct_without_payout") {
        setErrFn(
          isCompanyBalanceFail(item) || isCompanyBalanceFail(apiErr)
            ? DRIVER_COMPANY_BALANCE_MSG
            : apiErr ||
                (st === "deduct_without_payout"
                  ? "Düşüm yapıldı, gönderim yapılmadı."
                  : st === "failed"
                    ? "Yandex düşümü başarısız. Gönderim yapılmadı."
                    : "Gönderim başarısız.")
        );
      } else if (isWallet && (st === "payout_pending" || st === "payout_sending" || st === "awaiting_payout")) {
        setOkFn("Sipay cüzdan ödemesi iletildi, sonuç bekleniyor...");
        startStatusPoll(item.id, payoutMethod);
      } else {
        setErrFn(
          isCompanyBalanceFail(item) || isCompanyBalanceFail(apiErr)
            ? DRIVER_COMPANY_BALANCE_MSG
            : apiErr ||
              "Gönderim yanıtı alınamadı. İşlem geçmişini kontrol edin; başarılı yazılmadı."
        );
      }
      if (isWallet) setWalletAccepted(false);
      else {
        setIbanAccepted(false);
        saveIbanForm(iban, holder, nationalId);
      }
      await reloadAll();
    } catch (e2) {
      setErrFn(
        isCompanyBalanceFail(e2?.message) ? DRIVER_COMPANY_BALANCE_MSG : e2?.message || "Talep olusturulamadi."
      );
    } finally {
      setBusy(false);
    }
  }

  async function saveAutoSchedule(e) {
    e.preventDefault();
    if (autoBusy) return;
    setAutoErr("");
    setAutoOk("");
    try {
      setAutoBusy(true);
      if (!autoAccepted) {
        setAutoErr("Otomatik talimat icin hesap onayini isaretleyin.");
        return;
      }
      if (autoLocked) {
        setAutoErr("Otomatik ödeme talimatları bakımda.");
        return;
      }
      if (autoPayoutMethod === "iban" && ibanLocked) {
        setAutoErr("IBAN para çekimi bakımda. Talimat için Sipay cüzdan seçin.");
        return;
      }
      if (autoPayoutMethod === "wallet") {
        if (!autoWalletOk) {
          setAutoErr("Sipay cuzdan bulunamadi. Telefonunuzla kayitli cuzdan gerekli.");
          return;
        }
      } else if (!autoIban || !autoHolder || (ibanNationalIdRequired && !autoNationalOk)) {
        setAutoErr(
          ibanNationalIdRequired
            ? "IBAN, hesap sahibi ve TCKN/VKN alanlarini doldurun."
            : "IBAN ve hesap sahibi alanlarini doldurun."
        );
        return;
      }
      if (autoSecondSlotEnabled && !autoTimesOk) {
        setAutoErr("1. ve 2. saat farkli olmali.");
        return;
      }
      const payload = {
        enabled: true,
        frequency: autoFrequency,
        morning_hour: Number(autoTime1Hour),
        morning_minute: Number(autoTime1Minute),
        evening_hour: Number(autoTime2Hour),
        evening_minute: Number(autoTime2Minute),
        second_slot_enabled: autoSecondSlotEnabled,
        weekday: autoFrequency === "weekly" ? Number(autoWeekday) : null,
        day_of_month: autoFrequency === "monthly" ? Number(autoDayOfMonth) : null,
        payout_method: autoPayoutMethod,
        iban: autoPayoutMethod === "iban" ? autoIban : "",
        account_holder: autoHolder || (autoPayoutMethod === "wallet" ? "Sipay Cuzdan" : ""),
        national_id:
          autoPayoutMethod === "iban" && ibanNationalIdRequired ? autoNationalId : "",
        withdraw_full_balance: true,
      };
      const res = await upsertAutoWithdrawSchedule(payload);
      setSavedSchedule(res?.schedule || null);
      setAutoOk(`Otomatik odeme talimati aktif: ${scheduleSummary(res?.schedule)}`);
    } catch (e2) {
      setAutoErr(e2?.message || "Talimat kaydedilemedi.");
    } finally {
      setAutoBusy(false);
    }
  }

  async function removeAutoSchedule() {
    if (autoBusy) return;
    setAutoErr("");
    setAutoOk("");
    try {
      setAutoBusy(true);
      await deleteAutoWithdrawSchedule();
      setSavedSchedule(null);
      setAutoOk(
        savedSchedule?.source === "admin_mandate"
          ? "Yonetim odeme talimati kaldirildi."
          : "Otomatik odeme talimati iptal edildi."
      );
    } catch (e2) {
      setAutoErr(e2?.message || "Talimat iptal edilemedi.");
    } finally {
      setAutoBusy(false);
    }
  }

  const historySummary =
    rows.length > 0
      ? `${rows.length} islem kaydi · Son: ${trDate(rows[0]?.created_at)}`
      : "Henuz cekim talebi yok";

  const autoCardDescription = isAdminMandate
    ? `Yonetim talimati aktif: her gun 09:00 ve 21:00 · ${
        savedSchedule?.payout_method === "wallet" ? "Sipay cuzdan" : "IBAN"
      }`
    : hasActiveSchedule
    ? `Aktif: ${scheduleSummary(savedSchedule)}${
        savedSchedule?.payout_method === "wallet" ? " · Sipay cuzdan" : " · IBAN"
      }`
    : "Belirlediginiz saatte otomatik cekim talimati olusturun";

    return (
    <div className={`driver-page-wrap${payoutsLocked ? " driver-page-wrap--withdraw-locked" : ""}`}>
      {payoutsLocked ? (
      <div className="withdraw-maintenance-overlay" role="status" aria-live="polite">
        <div className="withdraw-maintenance-card">
          <div className="withdraw-maintenance-title" style={{ whiteSpace: "pre-line" }}>
            {MANUAL_WITHDRAW_BANNER}
          </div>
        </div>
      </div>
      ) : null}
      {manualRulesOn ? (
        <div
          role="status"
          style={{
            marginBottom: 12,
            padding: "12px 14px",
            borderRadius: 10,
            background: "#fef3c7",
            border: "1px solid #f59e0b",
            color: "#92400e",
            fontWeight: 800,
            fontSize: 14,
            lineHeight: 1.45,
            whiteSpace: "pre-line",
          }}
        >
          {MANUAL_WITHDRAW_BANNER}
        </div>
      ) : null}
      <div className="driver-page-title">Para Cek</div>
      <div className="driver-page-sub">
        Kartlara tiklayarak IBAN, Sipay cuzdan, otomatik talimat ve islem gecmisine ulasabilirsiniz.
      </div>
      <div style={{ fontWeight: 700, fontSize: 13, color: "#92400e", marginTop: 8 }}>
        {MIN_WITHDRAW_MSG}
        {belowMinWithdraw ? ` Şu anki çekilebilir bakiye: ${fmtMoney(amountNum)} ₺.` : ""}
      </div>

      <div className="withdraw-expandable-grid">
        <ExpandableEventCard
          cardId="iban-withdraw"
          headerVariant="iban"
          title="IBAN'A PARA ÇEK"
          description="Banka hesabiniza manuel para cekim talebi gonderin"
          locked={ibanLocked || manualBlocked}
          lockedLabel={ibanLocked ? "Bakımda" : manualLockLabel}
          content={
            <form className="driver-form-card" onSubmit={(e) => submitWithdrawal("iban", e)}>
              <label className="label">IBAN</label>
              <input
                className="input"
                value={iban}
                onChange={(e) => setIban(normalizeIbanInput(e.target.value))}
                onPaste={(e) => {
                  e.preventDefault();
                  const text = e.clipboardData?.getData("text") || "";
                  setIban(normalizeIbanInput(text));
                }}
                placeholder="TRxxxxxxxxxxxxxxxxxxxxxxxx"
                inputMode="text"
                autoComplete="off"
                spellCheck={false}
              />

              <label className="label">Hesap sahibi adi</label>
              <input
                className="input"
                value={holder}
                onChange={(e) => setHolder(e.target.value)}
                placeholder="Ad Soyad"
              />

              {ibanNationalIdRequired ? (
                <>
                  <label className="label">TCKN / VKN</label>
                  <input
                    className="input"
                    value={nationalId}
                    onChange={(e) => setNationalId(normalizeNationalIdInput(e.target.value))}
                    inputMode="numeric"
                    maxLength={11}
                  />
                </>
              ) : null}

              <div className="auto-schedule-balance-note">
                {MIN_WITHDRAW_MSG}{" "}
                {belowMinWithdraw ? (
                  <>Şu anki çekilebilir bakiye: {fmtMoney(amountNum)} ₺. Bu tutar çekilemez.</>
                ) : withdrawable > FIXED_FEE_IBAN ? (
                  <>
                    Cekilecek: {fmtMoney(amountNum)} ₺ · Hizmet Bedeli: {fmtMoney(feeIban)} ₺ · Hesabiniza
                    gececek: {fmtMoney(netIban)} ₺
                  </>
                ) : (
                  <>Simdiki cekilebilir bakiye: {fmtMoney(withdrawable)} ₺ (hizmet bedeli icin yetersiz)</>
                )}
              </div>

              <label className="driver-check-row">
                <input
                  type="checkbox"
                  checked={ibanAccepted}
                  onChange={(e) => setIbanAccepted(e.target.checked)}
                />
                <span style={{ fontSize: 13 }}>Banka hesabinin bana ait oldugunu onayliyorum</span>
              </label>

              {ibanErr ? <div style={{ color: "#b91c1c", fontWeight: 700 }}>{ibanErr}</div> : null}
              {ibanOk ? <div style={{ color: "#065f46", fontWeight: 700 }}>{ibanOk}</div> : null}

              <button
                type="submit"
                className="btn"
                disabled={busy || !canSubmitIban}
                style={{ marginTop: 4, maxWidth: 280 }}
              >
                {busy ? "Yanıt bekleniyor..." : "IBAN'a Para Cek"}
              </button>
            </form>
          }
        />

        <ExpandableEventCard
          cardId="wallet-withdraw"
          headerVariant="wallet"
          title="SİPAY CÜZDANA PARA ÇEK"
          description="Sipay cuzdaniniza hizli manuel odeme talebi gonderin"
          locked={manualBlocked}
          lockedLabel={manualLockLabel}
          content={
            <form className="driver-form-card" onSubmit={(e) => submitWithdrawal("wallet", e)}>
              <label className="label">Sipay cuzdan no</label>
              <input
                className="input"
                value={walletBusy ? "Sorgulaniyor..." : walletNumber || walletMasked || ""}
                readOnly
                placeholder="Telefonunuzla cuzdan araniyor"
              />
              <label className="label">Hesap sahibi adi</label>
              <input
                className="input"
                value={walletHolder}
                onChange={(e) => setWalletHolder(e.target.value)}
              />
              {walletErr ? (
                <div style={{ color: "#b91c1c", fontWeight: 700, fontSize: 13 }}>{walletErr}</div>
              ) : (
                <div className="muted" style={{ marginTop: -6 }}>
                  Cuzdan no, giris telefonunuz ile Sipay&apos;den otomatik cekilir.
                </div>
              )}

              <div className="auto-schedule-balance-note">
                {MIN_WITHDRAW_MSG}{" "}
                {belowMinWithdraw ? (
                  <>Şu anki çekilebilir bakiye: {fmtMoney(amountNum)} ₺. Bu tutar çekilemez.</>
                ) : withdrawable > FIXED_FEE_WALLET ? (
                  <>
                    Cekilecek: {fmtMoney(amountNum)} ₺ · Hizmet Bedeli: {fmtMoney(feeWallet)} ₺ · Hesabiniza
                    gececek: {fmtMoney(netWallet)} ₺
                  </>
                ) : (
                  <>Simdiki cekilebilir bakiye: {fmtMoney(withdrawable)} ₺ (hizmet bedeli icin yetersiz)</>
                )}
              </div>

              <label className="driver-check-row">
                <input
                  type="checkbox"
                  checked={walletAccepted}
                  onChange={(e) => setWalletAccepted(e.target.checked)}
                />
                <span style={{ fontSize: 13 }}>Sipay cuzdaninin bana ait oldugunu onayliyorum</span>
              </label>

              {walletOk ? <div style={{ color: "#065f46", fontWeight: 700 }}>{walletOk}</div> : null}

              <button
                type="submit"
                className="btn"
                disabled={busy || walletBusy || !canSubmitWallet}
                style={{ marginTop: 4, maxWidth: 280 }}
              >
                {busy ? "Yanıt bekleniyor..." : "Cuzdana Para Cek"}
              </button>
            </form>
          }
        />

        <ExpandableEventCard
          cardId="auto-schedule"
          headerVariant="auto"
          title="OTOMATİK ÖDEME TALİMATI"
          description={autoCardDescription}
          locked={autoLocked}
          lockedLabel="Bakımda"
          content={
            <form className="driver-form-card" onSubmit={saveAutoSchedule}>
              {isAdminMandate ? (
                <>
                  <div style={{ fontWeight: 900, fontSize: 16, color: "#166534" }}>
                    Yonetim odeme talimati aktif
                  </div>
                  <div className="muted" style={{ marginTop: 6 }}>
                    Yonetim tarafindan her gun 09:00 ve 21:00 (Istanbul) — bakiyeniz komisyon tutarinin uzerindeyse tum bakiyeniz{" "}
                    {savedSchedule?.payout_method === "wallet" ? "Sipay cuzdaniniza" : "IBAN hesabiniza"}{" "}
                    gonderilir. Isterseniz asagidan talimati kaldirabilirsiniz.
                  </div>
                  {savedSchedule?.iban_masked ? (
                    <div className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                      IBAN: {savedSchedule.iban_masked}
                      {savedSchedule.wallet_number_masked
                        ? ` · Cuzdan: ${savedSchedule.wallet_number_masked}`
                        : ""}
                    </div>
                  ) : null}
                  {autoErr ? <div style={{ color: "#b91c1c", fontWeight: 700 }}>{autoErr}</div> : null}
                  {autoOk ? <div style={{ color: "#065f46", fontWeight: 700 }}>{autoOk}</div> : null}
                  <button
                    type="button"
                    className="btn"
                    disabled={autoBusy}
                    onClick={removeAutoSchedule}
                    style={{ maxWidth: 280, background: "#dc2626", color: "#fff", marginTop: 10 }}
                  >
                    {autoBusy ? "Kaldırılıyor..." : "Talimatı Kaldır"}
                  </button>
                </>
              ) : (
                <>
              <div className="muted" style={{ marginBottom: 4 }}>
                Bilgileri doldurup kaydedince talimat aktif olur. Secilen gunlerde belirlediginiz saatte
                otomatik cekim baslatilir; istege bagli ikinci saat eklenebilir.
              </div>

              <PayoutMethodSwitch
                value={autoPayoutMethod}
                onChange={onAutoPayoutMethodChange}
                disabled={autoBusy || autoWalletBusy}
                ibanLocked={ibanLocked}
              />

              {autoPayoutMethod === "iban" ? (
          <>
            <label className="label">IBAN</label>
            <input
              className="input"
              value={autoIban}
              onChange={(e) => setAutoIban(normalizeIbanInput(e.target.value))}
              onPaste={(e) => {
                e.preventDefault();
                const text = e.clipboardData?.getData("text") || "";
                setAutoIban(normalizeIbanInput(text));
              }}
              placeholder="TRxxxxxxxxxxxxxxxxxxxxxxxx"
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
            />

            <label className="label">Hesap sahibi adi</label>
            <input
              className="input"
              value={autoHolder}
              onChange={(e) => setAutoHolder(e.target.value)}
              placeholder="Ad Soyad"
            />

            {ibanNationalIdRequired ? (
              <>
                <label className="label">TCKN / VKN</label>
                <input
                  className="input"
                  value={autoNationalId}
                  onChange={(e) => setAutoNationalId(normalizeNationalIdInput(e.target.value))}
                  inputMode="numeric"
                  maxLength={11}
                />
              </>
            ) : null}
          </>
        ) : (
          <>
            <label className="label">Sipay cuzdan no</label>
            <input
              className="input"
              value={
                autoWalletBusy
                  ? "Sorgulanıyor..."
                  : autoWalletNumber || autoWalletMasked || ""
              }
              readOnly
              placeholder="Telefonunuzla cuzdan araniyor"
            />
            <label className="label">Hesap sahibi adi</label>
            <input
              className="input"
              value={autoHolder}
              onChange={(e) => setAutoHolder(e.target.value)}
            />
            {autoWalletErr ? (
              <div style={{ color: "#b91c1c", fontWeight: 700, fontSize: 13 }}>
                {autoWalletErr}
              </div>
            ) : (
              <div className="muted" style={{ marginTop: -6 }}>
                Cuzdan no, giris telefonunuz ile Sipay&apos;den otomatik cekilir.
              </div>
            )}
          </>
        )}

        <div className="auto-schedule-row auto-schedule-row-period">
          <div className="auto-schedule-field">
            <label className="label">Periyot</label>
            <select
              className="input"
              value={autoFrequency}
              onChange={(e) => setAutoFrequency(e.target.value)}
            >
              <option value="daily">Günlük</option>
              <option value="every_2_days">2 günde bir</option>
              <option value="every_3_days">3 günde bir</option>
              <option value="weekly">Haftalık</option>
              <option value="monthly">Aylık</option>
            </select>
          </div>

          <div className="auto-schedule-field">
            <label className="label">
              {autoFrequency === "monthly" ? "Ayın günü" : "Gün"}
            </label>
            {autoFrequency === "weekly" ? (
              <select
                className="input"
                value={autoWeekday}
                onChange={(e) => setAutoWeekday(Number(e.target.value))}
              >
                {WEEKDAYS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            ) : autoFrequency === "monthly" ? (
              <MonthDayPicker
                pickerId="driver-mandate-day"
                value={autoDayOfMonth}
                onChange={setAutoDayOfMonth}
                disabled={autoBusy}
              />
            ) : (
              <select className="input" value="every" disabled>
                <option value="every">
                  {autoFrequency === "every_2_days"
                    ? "2 günde bir"
                    : autoFrequency === "every_3_days"
                      ? "3 günde bir"
                      : "Her gün"}
                </option>
              </select>
            )}
          </div>
          {autoFrequency === "monthly" ? (
            <div className="month-day-hint">
              Seçilen gün o ay yoksa ödeme bir önceki güne alınır.
            </div>
          ) : null}
        </div>

        <div className="auto-schedule-time-block">
          <ScheduleTimePicker
            pickerId="slot1"
            hour={autoTime1Hour}
            minute={autoTime1Minute}
            onHourChange={setAutoTime1Hour}
            onMinuteChange={setAutoTime1Minute}
            disabled={autoBusy}
          />
        </div>

        <div className="auto-schedule-add-row">
          <span className="auto-schedule-add-label">saat ekle</span>
          <button
            type="button"
            className={`auto-schedule-add-btn${autoSecondSlotEnabled ? " is-active" : ""}`}
            onClick={() => setAutoSecondSlotEnabled((v) => !v)}
            aria-expanded={autoSecondSlotEnabled}
            aria-label={autoSecondSlotEnabled ? "Ikinci saati kaldir" : "Ikinci saat ekle"}
          >
            <motion.span
              className="auto-schedule-add-btn-icon"
              animate={{ rotate: autoSecondSlotEnabled ? 45 : 0 }}
              transition={{ type: "spring", duration: 0.3, bounce: 0.15 }}
            >
              +
            </motion.span>
          </button>
        </div>

        <AnimatePresence initial={false}>
          {autoSecondSlotEnabled ? (
            <motion.div
              key="auto-second-slot"
              className="auto-schedule-time-block auto-schedule-time-block--expandable"
              initial={{ height: 0, opacity: 0, marginTop: 0 }}
              animate={{ height: "auto", opacity: 1, marginTop: 8 }}
              exit={{ height: 0, opacity: 0, marginTop: 0 }}
              transition={{ type: "spring", duration: 0.38, bounce: 0.12 }}
              style={{ overflow: "hidden" }}
            >
              <ScheduleTimePicker
                pickerId="slot2"
                hour={autoTime2Hour}
                minute={autoTime2Minute}
                onHourChange={setAutoTime2Hour}
                onMinuteChange={setAutoTime2Minute}
                disabled={autoBusy}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>

        <div className="auto-schedule-balance-note">
          {MIN_WITHDRAW_MSG}{" "}
          {autoPayoutMethod === "wallet"
            ? `Hizmet Bedeli: ${fmtMoney(FIXED_FEE_WALLET)} ₺ kesilir.`
            : `Hizmet Bedeli: ${fmtMoney(FIXED_FEE_IBAN)} ₺ kesilir.`}
          {belowMinWithdraw ? (
            <> Şu anki çekilebilir bakiye: {fmtMoney(amountNum)} ₺. Talimat saatinde bakiye 150 TL altındaysa ödeme gitmez.</>
          ) : (
            <> Şimdiki çekilebilir bakiye: {fmtMoney(withdrawable)} ₺</>
          )}
        </div>
        {autoSecondSlotEnabled && !autoTimesOk ? (
          <div style={{ color: "#b45309", fontWeight: 700, fontSize: 13 }}>
            1. ve 2. saat farkli olmali.
          </div>
        ) : null}

        <label className="driver-check-row">
          <input
            type="checkbox"
            checked={autoAccepted}
            onChange={(e) => setAutoAccepted(e.target.checked)}
          />
          <span style={{ fontSize: 13 }}>
            {autoPayoutMethod === "wallet"
              ? "Sipay cuzdaninin bana ait oldugunu ve otomatik cekimlere izin verdigimi onayliyorum"
              : "Banka hesabinin bana ait oldugunu ve otomatik cekimlere izin verdigimi onayliyorum"}
          </span>
        </label>

        {autoErr ? <div style={{ color: "#b91c1c", fontWeight: 700 }}>{autoErr}</div> : null}
        {autoOk ? <div style={{ color: "#065f46", fontWeight: 700 }}>{autoOk}</div> : null}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
          {!hasActiveSchedule ? (
            <button
              type="submit"
              className="btn"
              disabled={autoBusy || autoWalletBusy || !canSubmitAuto}
              style={{ maxWidth: 280 }}
            >
              {autoBusy ? "Kaydediliyor..." : "Talimati Kaydet"}
            </button>
          ) : (
            <button
              type="button"
              className="btn"
              disabled={autoBusy}
              onClick={removeAutoSchedule}
              style={{ maxWidth: 280, background: "#dc2626", color: "#fff" }}
            >
              {autoBusy ? "Iptal ediliyor..." : "Otomatik odemeyi iptal et"}
            </button>
          )}
        </div>
                </>
              )}
            </form>
          }
        />

        <ExpandableEventCard
          cardId="withdraw-history"
          headerVariant="history"
          title="İŞLEM GEÇMİŞİ"
          description={historySummary}
          content={
            <div className="driver-table-card">
              <div className="driver-table-card-title">ISLEM GECMISI</div>
              <div className="driver-table-head">
                <div>Tarih</div>
                <div style={{ textAlign: "right" }}>Talep</div>
                <div style={{ textAlign: "right" }}>Hizmet Bedeli</div>
                <div style={{ textAlign: "right" }}>Hesaba gececek</div>
                <div style={{ textAlign: "center" }}>Durum</div>
                <div className="driver-table-help">
                  IBAN ve cüzdan takip bilgisi satirin altinda gosterilir.
                </div>
              </div>
              {(rows || []).map((r) => (
                <div key={r.id} className="driver-table-row">
                  <div>{trDate(r.created_at)}</div>
                  <div style={{ textAlign: "right", fontWeight: 800 }}>{fmtMoney(r.amount)} ₺</div>
                  <div style={{ textAlign: "right", fontWeight: 700 }}>{fmtMoney(r.fee)} ₺</div>
                  <div style={{ textAlign: "right", fontWeight: 900, color: "#065f46" }}>
                    {fmtMoney(r.net_amount)} ₺
                  </div>
                  <div style={{ textAlign: "center", fontWeight: 700 }}>
                    {statusLabel(r.status, r)}
                  </div>
                  <div className="driver-row-meta">
                    Talep No: <b>{r.request_uid || r.id || "-"}</b>
                    {r.source === "auto_schedule" ? " · Otomatik" : null}
                    {trackingBits(r).map((bit) => (
                      <span key={`${bit.label}-${bit.value}`}>
                        {" · "}
                        {bit.label}: <b>{bit.value}</b>
                      </span>
                    ))}
                  </div>
                  <div className="driver-row-submeta">
                    {payoutStepLabel(r)} ·{" "}
                    {String(r.payout_method || "").toLowerCase() === "wallet"
                      ? `Cuzdan: ${r.wallet_number_masked || "-"}`
                      : `IBAN: ${r.iban_masked || "-"}`}
                  </div>
                  {failReasonText(r) ? (
                    <div className="driver-row-submeta" style={{ color: "#b91c1c", fontWeight: 700 }}>
                      IBAN basarisizlik nedeni: {failReasonText(r)}
                    </div>
                  ) : null}
                </div>
              ))}
              {!rows.length ? (
                <div style={{ padding: "12px 14px", color: "#6b7280" }}>Henuz cekim talebi yok.</div>
              ) : null}
            </div>
          }
        />
      </div>
    </div>
  );
}
